import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarIcon, Download, BarChart3, Clock, MapPin, TrendingUp, Eye } from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, parseISO, eachDayOfInterval } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Room, ScheduleItem, Doctor, Staff } from './SchedulerTypes';

interface SlotManagementReportProps {
  scheduleItems: ScheduleItem[];
  rooms: Room[];
  doctors: Doctor[];
  staff: Staff[];
  currentDate: Date;
}

type DateRangeType = 'daily' | 'weekly' | 'monthly' | 'custom';

interface DateRange {
  from: Date;
  to: Date;
}

interface SlotStats {
  totalSlots: number;
  occupiedSlots: number;
  emptySlots: number;
  occupancyRate: number;
}

interface RoomSlotStats extends SlotStats {
  roomId: string;
  roomName: string;
}

interface TimeSlot {
  hour: number;
  minute: number;
  timeString: string;
}

interface OccupiedSlotDetail {
  date: string;
  time: string;
  doctor: string;
  staff: string;
  procedure: string;
  duration: string;
}

const SlotManagementReport: React.FC<SlotManagementReportProps> = ({
  scheduleItems,
  rooms,
  doctors,
  staff,
  currentDate,
}) => {
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('weekly');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<RoomSlotStats | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Generate 15-minute time slots for weekdays: 9AM-12PM and 1PM-6PM (32 slots total)
  const timeSlots = useMemo((): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    // Morning: 9:00–12:00 (3 hours)
    for (let hour = 9; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push({
          hour,
          minute,
          timeString: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        });
      }
    }
    // Afternoon: 1:00–6:00 (5 hours)
    for (let hour = 13; hour < 18; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push({
          hour,
          minute,
          timeString: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        });
      }
    }
    return slots; // 8 hours × 4 slots/hour = 32 slots
  }, []);

  // Generate Saturday-only time slots (9:00 AM to 1:00 PM, no lunch break)
  const saturdayTimeSlots = useMemo((): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let hour = 9; hour < 13; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push({
          hour,
          minute,
          timeString: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        });
      }
    }
    return slots;
  }, []);

  // Get time slots for a specific day
  const getTimeSlotsForDay = (date: Date): TimeSlot[] => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek === 0) return []; // Sunday - no working hours
    return dayOfWeek === 6 ? saturdayTimeSlots : timeSlots;
  };

  // Calculate duration between start and end time
  const calculateDuration = (startTime: string, endTime: string): string => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const durationMinutes = endMinutes - startMinutes;
    
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  // Calculate date range based on type
  const dateRange = useMemo((): DateRange => {
    switch (dateRangeType) {
      case 'daily':
        return { from: currentDate, to: currentDate };
      case 'weekly':
        return {
          from: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
          to: endOfWeek(currentDate, { weekStartsOn: 1 }),
        };
      case 'monthly':
        return {
          from: startOfMonth(currentDate),
          to: endOfMonth(currentDate),
        };
      case 'custom':
        return (customDateRange && customDateRange.from && customDateRange.to) 
          ? customDateRange 
          : { from: currentDate, to: currentDate };
      default:
        return { from: currentDate, to: currentDate };
    }
  }, [currentDate, dateRangeType, customDateRange]);

  // Get days in the range (excluding Sundays - non-working days)
  const daysInRange = useMemo(() => {
    try {
      // Ensure both dates are valid Date objects
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      
      console.log('🔍 Date interval debug:');
      console.log('  - fromDate:', fromDate, 'valid:', !isNaN(fromDate.getTime()));
      console.log('  - toDate:', toDate, 'valid:', !isNaN(toDate.getTime()));
      
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        console.log('  - Invalid dates detected, returning empty array');
        return [];
      }
      
      if (fromDate > toDate) {
        console.log('  - fromDate > toDate, swapping');
        const allDays = eachDayOfInterval({ start: toDate, end: fromDate });
        return allDays.filter(day => day.getDay() !== 0); // Exclude Sundays
      }
      
      const allDays = eachDayOfInterval({ start: fromDate, end: toDate });
      const workingDays = allDays.filter(day => day.getDay() !== 0); // Exclude Sundays (day 0)
      
      console.log('  - All days:', allDays.length);
      console.log('  - Working days (no Sundays):', workingDays.length);
      
      return workingDays;
    } catch (error) {
      console.error('Error calculating days in range:', error);
      return [];
    }
  }, [dateRange]);

  // Filter active rooms
  const activeRooms = useMemo(() => {
    return rooms.filter(room => room.status === 'active');
  }, [rooms]);

  // Check if a time slot overlaps with a schedule item
  const isSlotOccupied = (date: Date, roomId: string, slot: TimeSlot): boolean => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const slotStartTime = slot.timeString;
    const slotEndHour = slot.minute === 45 ? slot.hour + 1 : slot.hour;
    const slotEndMinute = slot.minute === 45 ? 0 : slot.minute + 15;
    const slotEndTime = `${slotEndHour.toString().padStart(2, '0')}:${slotEndMinute.toString().padStart(2, '0')}`;

    const matchingItems = scheduleItems.filter(item => 
      item.date === dateStr && item.roomId === roomId && item.type === 'appointment'
    );

    // Enhanced debugging for the first few slots
    const shouldDebug = slot.hour >= 9 && slot.hour <= 11 && dateStr.includes('2025-09') && matchingItems.length > 0;

    if (shouldDebug && slot.minute === 0) {
      console.log(`\n🧪 DETAILED SLOT TEST - ${dateStr} Room:${roomId} Time:${slot.timeString}`);
      console.log(`📋 Found ${matchingItems.length} appointments for this room/date:`);
      matchingItems.forEach((item, i) => {
        const duration = calculateDuration(item.startTime, item.endTime);
        console.log(`  ${i+1}. ${item.startTime}-${item.endTime} (${duration}) - "${item.title}"`);
      });
    }

    const isOccupied = matchingItems.some(item => {
      // Check if schedule item overlaps with this time slot
      const itemStart = item.startTime;
      const itemEnd = item.endTime;

      // Convert times to minutes for easier comparison
      const slotStartMinutes = slot.hour * 60 + slot.minute;
      const slotEndMinutes = slotEndHour * 60 + slotEndMinute;
      
      const [itemStartHour, itemStartMin] = itemStart.split(':').map(Number);
      const [itemEndHour, itemEndMin] = itemEnd.split(':').map(Number);
      const itemStartMinutes = itemStartHour * 60 + itemStartMin;
      const itemEndMinutes = itemEndHour * 60 + itemEndMin;

      // Check for overlap
      const overlaps = itemStartMinutes < slotEndMinutes && itemEndMinutes > slotStartMinutes;
      
      if (shouldDebug) {
        console.log(`  ⏰ Testing slot ${slot.timeString}-${slotEndTime} (${slotStartMinutes}-${slotEndMinutes} mins)`);
        console.log(`     vs appointment ${itemStart}-${itemEnd} (${itemStartMinutes}-${itemEndMinutes} mins)`);
        console.log(`     Overlap check: ${itemStartMinutes} < ${slotEndMinutes} && ${itemEndMinutes} > ${slotStartMinutes} = ${overlaps}`);
      }
      
      return overlaps;
    });

    if (shouldDebug) {
      console.log(`  🎯 Result: Slot ${slot.timeString} is ${isOccupied ? 'OCCUPIED' : 'FREE'}\n`);
    }

    return isOccupied;
  };

  // Calculate overall statistics
  const overallStats = useMemo((): SlotStats => {
    console.log('🔍 SlotManagementReport Debug:');
    console.log('  - Schedule Items:', scheduleItems.length);
    console.log('  - Appointment Type Items:', scheduleItems.filter(item => item.type === 'appointment').length);
    console.log('  - Active Rooms:', activeRooms.length, activeRooms.map(r => r.name));
    console.log('  - Room IDs:', activeRooms.map(r => r.id));
    console.log('  - Weekday Time Slots:', timeSlots.length, '(should be 32: 9AM-12PM and 1PM-6PM = 8hrs × 4 = 32)');
    console.log('  - Saturday Time Slots:', saturdayTimeSlots.length, '(should be 16: 9AM-1PM)');
    console.log('  - Days in Range:', daysInRange.length);
    console.log('  - Date Range:', format(dateRange.from, 'yyyy-MM-dd'), 'to', format(dateRange.to, 'yyyy-MM-dd'));
    
    // Sample schedule items for debugging
    const sampleItems = scheduleItems.filter(item => item.type === 'appointment').slice(0, 3);
    console.log('  - Sample appointment items:', sampleItems.map(item => ({
      date: item.date,
      roomId: item.roomId,
      time: `${item.startTime}-${item.endTime}`,
      title: item.title
    })));
    
    // Calculate total slots considering different schedules for different days
    let totalSlots = 0;
    let occupiedSlots = 0;

    daysInRange.forEach(date => {
      const dayTimeSlots = getTimeSlotsForDay(date);
      const dayOfWeek = date.getDay();
      const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek];
      
      let scheduleInfo = '';
      if (dayOfWeek === 0) scheduleInfo = ' (CLOSED - no slots)';
      else if (dayOfWeek === 6) scheduleInfo = ' (Saturday: 9AM-1PM)';
      else scheduleInfo = ' (Weekday: 9AM-12PM, 1PM-6PM)';
      
      console.log(`  - ${dayName} (${format(date, 'yyyy-MM-dd')}): ${dayTimeSlots.length} slots per room${scheduleInfo}`);
      
      activeRooms.forEach(room => {
        totalSlots += dayTimeSlots.length;
        
        // Count each individual 15-minute slot that is occupied
        dayTimeSlots.forEach(slot => {
          if (isSlotOccupied(date, room.id, slot)) {
            occupiedSlots++;
          }
        });
      });
    });

    console.log('  - Slot-based counting method: Each 15-minute slot counted individually');
    console.log('  - Example: 1-hour appointment = 4 occupied slots, 30-min appointment = 2 occupied slots');

    const emptySlots = totalSlots - occupiedSlots;
    const occupancyRate = totalSlots > 0 ? (occupiedSlots / totalSlots) * 100 : 0;

    console.log('  - Total Slots Calculated:', totalSlots);
    console.log('  - Occupied Slots (15-min slot count):', occupiedSlots);
    console.log('  - Empty Slots:', emptySlots);
    console.log('  - Occupancy Rate:', occupancyRate.toFixed(1) + '%');
    console.log('  - Schedule Items vs Slots: If you have 10 appointments of 1 hour each, that equals 40 occupied slots (10 × 4 slots)');

    return {
      totalSlots,
      occupiedSlots,
      emptySlots,
      occupancyRate,
    };
  }, [activeRooms, timeSlots, saturdayTimeSlots, daysInRange, isSlotOccupied, scheduleItems, dateRange, getTimeSlotsForDay]);

  // Calculate per-room statistics
  const roomStats = useMemo((): RoomSlotStats[] => {
    console.log('\n🏥 Per-Room Statistics Debug:');
    
    return activeRooms.map(room => {
      let totalSlots = 0;
      let occupiedSlots = 0;

      // Count appointments for this specific room across date range
      const roomAppointments = scheduleItems.filter(item => 
        item.roomId === room.id && 
        item.type === 'appointment' &&
        daysInRange.some(date => format(date, 'yyyy-MM-dd') === item.date)
      );

      console.log(`\n🏠 Room: ${room.name} (ID: ${room.id})`);
      console.log(`📅 Date range appointments: ${roomAppointments.length}`);
      
      if (roomAppointments.length > 0) {
        console.log(`📝 Sample appointments:`);
        roomAppointments.slice(0, 3).forEach((item, i) => {
          const duration = calculateDuration(item.startTime, item.endTime);
          console.log(`  ${i+1}. ${item.date} ${item.startTime}-${item.endTime} (${duration})`);
        });
      }

      daysInRange.forEach(date => {
        const dayTimeSlots = getTimeSlotsForDay(date);
        totalSlots += dayTimeSlots.length;
        
        let dailyOccupied = 0;
        let debuggedSlots = 0;
        
        dayTimeSlots.forEach(slot => {
          const occupied = isSlotOccupied(date, room.id, slot);
          if (occupied) {
            occupiedSlots++;
            dailyOccupied++;
          }
          
          // Debug first few slots to see what's happening
          if (debuggedSlots < 5 && roomAppointments.length > 0 && slot.hour >= 9 && slot.hour <= 10) {
            debuggedSlots++;
          }
        });
        
        if (dailyOccupied > 0) {
          console.log(`📊 ${format(date, 'MMM d')}: ${dailyOccupied}/${dayTimeSlots.length} slots occupied`);
        }
      });

      const emptySlots = totalSlots - occupiedSlots;
      const occupancyRate = totalSlots > 0 ? (occupiedSlots / totalSlots) * 100 : 0;

      console.log(`🎯 ${room.name} FINAL: ${occupiedSlots}/${totalSlots} slots = ${occupancyRate.toFixed(1)}%`);
      console.log(`📊 Expected vs Actual: ${roomAppointments.length} appointments → ${occupiedSlots} occupied slots`);

      return {
        roomId: room.id,
        roomName: room.name,
        totalSlots,
        occupiedSlots,
        emptySlots,
        occupancyRate,
      };
    }).sort((a, b) => b.occupancyRate - a.occupancyRate);
  }, [activeRooms, daysInRange, isSlotOccupied, getTimeSlotsForDay, scheduleItems, calculateDuration]);

  // Get occupied slot details for a specific room
  const getOccupiedSlotDetails = (roomId: string): OccupiedSlotDetail[] => {
    const details: OccupiedSlotDetail[] = [];
    
    daysInRange.forEach(date => {
      const dayTimeSlots = getTimeSlotsForDay(date);
      
      dayTimeSlots.forEach(slot => {
        if (isSlotOccupied(date, roomId, slot)) {
          const dateStr = format(date, 'yyyy-MM-dd');
          
          // Find the schedule item for this slot
          const scheduleItem = scheduleItems.find(item => {
            if (item.date !== dateStr || item.roomId !== roomId) return false;
            
            const [itemStartHour, itemStartMin] = item.startTime.split(':').map(Number);
            const [itemEndHour, itemEndMin] = item.endTime.split(':').map(Number);
            const itemStartMinutes = itemStartHour * 60 + itemStartMin;
            const itemEndMinutes = itemEndHour * 60 + itemEndMin;
            
            const slotStartMinutes = slot.hour * 60 + slot.minute;
            const slotEndHour = slot.minute === 45 ? slot.hour + 1 : slot.hour;
            const slotEndMinute = slot.minute === 45 ? 0 : slot.minute + 15;
            const slotEndMinutes = slotEndHour * 60 + slotEndMinute;
            
            return itemStartMinutes < slotEndMinutes && itemEndMinutes > slotStartMinutes;
          });
          
          if (scheduleItem) {
            const doctor = doctors.find(d => d.id === scheduleItem.doctorId);
            const staffMember = staff.find(s => s.id === scheduleItem.staffId);
            
            details.push({
              date: format(date, 'MMM d, yyyy'),
              time: `${scheduleItem.startTime} - ${scheduleItem.endTime}`,
              doctor: doctor?.name || 'Unknown Doctor',
              staff: staffMember?.name || 'Unknown Staff',
              procedure: scheduleItem.title || 'No procedure specified',
              duration: calculateDuration(scheduleItem.startTime, scheduleItem.endTime),
            });
          }
        }
      });
    });
    
    return details.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const handleRoomClick = (room: RoomSlotStats) => {
    setSelectedRoom(room);
    setIsModalOpen(true);
  };

  const exportToCSV = () => {
    const csvData = [
      ['Room Name', 'Total Slots', 'Occupied Slots', 'Empty Slots', 'Occupancy Rate (%)'],
      ...roomStats.map(stat => [
        stat.roomName,
        stat.totalSlots,
        stat.occupiedSlots,
        stat.emptySlots,
        stat.occupancyRate.toFixed(1),
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `slot-management-report-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getOccupancyColor = (rate: number): string => {
    if (rate >= 80) return 'text-red-600';
    if (rate >= 60) return 'text-orange-600';
    if (rate >= 40) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getOccupancyBadgeVariant = (rate: number): "default" | "destructive" | "outline" | "secondary" => {
    if (rate >= 80) return 'destructive';
    if (rate >= 60) return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5" />
            <span>Date Range Filter</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">Range:</label>
              <Select value={dateRangeType} onValueChange={(value: DateRangeType) => setDateRangeType(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateRangeType === 'custom' && (
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customDateRange && customDateRange.from && customDateRange.to
                      ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d, yyyy')}`
                      : 'Select date range'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => range && setCustomDateRange(range as { from: Date; to: Date })}
                    numberOfMonths={2}
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={2020}
                    toYear={2030}
                  />
                </PopoverContent>
              </Popover>
            )}

            <div className="text-sm text-muted-foreground">
              {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
              <span className="ml-2">({daysInRange.length} working days, {activeRooms.length} rooms, no Sundays)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overall Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Slots</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallStats.totalSlots.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {activeRooms.length} rooms × {daysInRange.length} working days (44 M-F, 16 Sat, 0 Sun)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Occupied Slots</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{overallStats.occupiedSlots.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Scheduled appointments
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Empty Slots</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{overallStats.emptySlots.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Available for booking
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Occupancy Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getOccupancyColor(overallStats.occupancyRate)}`}>
              {overallStats.occupancyRate.toFixed(1)}%
            </div>
            <div className="mt-2">
              <Progress value={overallStats.occupancyRate} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Room-wise Statistics */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Room-wise Slot Utilization</span>
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Room Name</TableHead>
                  <TableHead className="text-center">Total Slots</TableHead>
                  <TableHead className="text-center">Occupied</TableHead>
                  <TableHead className="text-center">Empty</TableHead>
                  <TableHead className="text-center">Occupancy Rate</TableHead>
                  <TableHead className="text-center">Utilization</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roomStats.length > 0 ? (
                  roomStats.map((stat) => (
                    <TableRow key={stat.roomId}>
                      <TableCell className="font-medium">{stat.roomName}</TableCell>
                      <TableCell className="text-center">{stat.totalSlots.toLocaleString()}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{stat.occupiedSlots.toLocaleString()}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{stat.emptySlots.toLocaleString()}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={getOccupancyBadgeVariant(stat.occupancyRate)}>
                          {stat.occupancyRate.toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="w-full max-w-[100px] mx-auto">
                          <Progress value={stat.occupancyRate} className="h-2" />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRoomClick(stat)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No room data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Occupied Slot Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Occupied Slots Details - {selectedRoom?.roomName}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedRoom && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{selectedRoom.totalSlots}</div>
                    <p className="text-xs text-muted-foreground">Total Slots</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">{selectedRoom.occupiedSlots}</div>
                    <p className="text-xs text-muted-foreground">Occupied</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{selectedRoom.emptySlots}</div>
                    <p className="text-xs text-muted-foreground">Available</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className={`text-2xl font-bold ${getOccupancyColor(selectedRoom.occupancyRate)}`}>
                      {selectedRoom.occupancyRate.toFixed(1)}%
                    </div>
                    <p className="text-xs text-muted-foreground">Occupancy</p>
                  </CardContent>
                </Card>
              </div>

              {/* Occupied Slots Details Table */}
              <div className="border rounded-lg">
                <div className="p-4 border-b">
                  <h3 className="text-lg font-semibold">Occupied Appointment Slots</h3>
                  <p className="text-sm text-muted-foreground">
                    Showing all appointments in {selectedRoom.roomName} for the selected date range
                  </p>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Doctor</TableHead>
                      <TableHead>Staff</TableHead>
                      <TableHead>Procedure</TableHead>
                      <TableHead className="text-center">Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const occupiedSlots = getOccupiedSlotDetails(selectedRoom.roomId);
                      
                      if (occupiedSlots.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                              No occupied slots found for this room in the selected date range.
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return occupiedSlots.map((slot, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{slot.date}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{slot.time}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{slot.doctor}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{slot.staff}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <div className="truncate" title={slot.procedure}>
                              {slot.procedure}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="default">{slot.duration}</Badge>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (selectedRoom) {
                      const occupiedSlots = getOccupiedSlotDetails(selectedRoom.roomId);
                      const csvData = [
                        ['Date', 'Time', 'Doctor', 'Staff', 'Procedure', 'Duration'],
                        ...occupiedSlots.map(slot => [
                          slot.date,
                          slot.time,
                          slot.doctor,
                          slot.staff,
                          slot.procedure,
                          slot.duration,
                        ])
                      ];
                      
                      const csvContent = csvData.map(row => row.join(',')).join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      const url = URL.createObjectURL(blob);
                      link.setAttribute('href', url);
                      link.setAttribute('download', `occupied-slots-${selectedRoom.roomName}-${format(dateRange.from, 'yyyy-MM-dd')}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Export Details</span>
                </Button>
                <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SlotManagementReport;
